import { FC, useEffect, useRef } from 'react';

import styles from './index.module.scss';
import {
  CaretRightOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  PauseOutlined,
} from '@ant-design/icons';
import useVideoController from './useVideoController';
import classNames from 'classnames';
import { bindReplayRange } from './replayRange';

export interface IVideoPlayerProps {
  id: string;
  videoProps?: React.VideoHTMLAttributes<HTMLVideoElement>;
  videoSourceProps?: React.SourceHTMLAttributes<HTMLSourceElement>;
  onCurrentPlayTimeChange?: (seconds: number) => void;
  initPlayTime?: number;
  onPlaybackEnded?: () => void;
}

const VideoPlayer: FC<IVideoPlayerProps> = ({
  id,
  videoProps = {},
  videoSourceProps = {},
  onCurrentPlayTimeChange,
  initPlayTime,
  onPlaybackEnded,
}) => {
  const endedCallback = useRef(onPlaybackEnded);
  endedCallback.current = onPlaybackEnded;
  const {
    isEnded,
    showController,
    isPlaying,
    handlePlayOrPause,
    currentPlayTime,
    currentPlayTimeString,
    totalDuration,
    isFullScreen,
    handleFullScreenChange,
  } = useVideoController({
    id,
  });

  const renderPlayPauseIcon = () => {
    if (isEnded) {
      return <span className={styles.isEndedText}>播放已结束</span>;
    }
    return isPlaying ? (
      <PauseOutlined
        className={classNames(styles.playIcon, styles.icon)}
        onClick={handlePlayOrPause}
      />
    ) : (
      <CaretRightOutlined
        className={classNames(styles.playIcon, styles.icon)}
        onClick={handlePlayOrPause}
      />
    );
  };

  const renderFullScreenIcon = () => {
    return isFullScreen ? (
      <FullscreenExitOutlined className={styles.icon} onClick={handleFullScreenChange} />
    ) : (
      <FullscreenOutlined className={styles.icon} onClick={handleFullScreenChange} />
    );
  };

  const renderControllerBar = () => {
    return (
      <div className={styles.controllerBar}>
        <div className={styles.leftButtons}>
          {renderPlayPauseIcon()}
          <div className={styles.time}>
            <span>{currentPlayTimeString}</span>/<span>{totalDuration}</span>
          </div>
        </div>
        <div>{renderFullScreenIcon()}</div>
      </div>
    );
  };

  useEffect(() => {
    onCurrentPlayTimeChange?.(currentPlayTime);
  }, [onCurrentPlayTimeChange, currentPlayTime]);

  useEffect(() => {
    const video = document.getElementById(id) as HTMLVideoElement;
    return bindReplayRange(video, videoSourceProps.src ?? '', initPlayTime ?? 0, () => endedCallback.current?.());
    // Saving progress updates parent callbacks; only a new source should initialize playback again.
  }, [id, videoSourceProps.src]);

  return (
    <div className={styles.videoPlayerContainer}>
      <video
        className={styles.videoTag}
        id={id}
        controls={false}
        width={'100%'}
        onClick={handlePlayOrPause}
        onDoubleClick={handleFullScreenChange}
        loop={false}
        {...videoProps}
      >
        <source {...videoSourceProps} />
      </video>
      {showController && renderControllerBar()}
    </div>
  );
};

export default VideoPlayer;
